import { LibraryCache } from 'base/library_cache';
import { SlotType } from 'base/library_service';
import { AnyMenuItem, menuItem, subMenu } from 'base/ui/menu_model';
import {
  BlocksDataAttribute,
  extractTreeByName,
  getDataAttr,
  getNodeName,
} from 'base/webflow/data_attributes';
import { Component, NestedNode, NestedTagNode } from 'base/webflow/schema';
import { styleGuideStyleById } from 'base/webflow/style_guide';
import { ElementNodeEditingActions } from 'blocks/editor/editing_actions/element_node';
import {
  anElementInstance,
  ElementInstance,
  ElementInstanceState,
  ElementSourceState,
  MarginState,
  MARGIN_DIRECTIONS,
  MARGIN_SIZES,
  SlotOverride,
} from 'blocks/editor/model';
import { parentElementWithinRoot } from 'blocks/editor/model_helpers';
import { computedFn } from 'mobx-utils';
import { doesInteractableTreeContainText, Interactable } from './interactable';

/** Information about what is accepted in a slot. */
export type SlotInfo = {
  name: string;
  nodeId: string;
  typeId: `ST${string}`;
  originalItems: { state: ElementInstanceState; rootNode: NestedNode }[];
  mechanism: 'self' | 'children';
};

/** Gets the canonical (master) element ID for a given element instance if it is loaded. */
export function getCanonicalElementId(
  element: ElementInstance | ElementInstanceState,
  library: LibraryCache,
): string | undefined {
  if (
    element.source.slug.startsWith('EL') &&
    element.source.rootName === undefined
  ) {
    return element.source.slug;
  }
  const component = library.getCachedPublicComponentJson(element.source.slug, {
    fetchIfMissing: true,
  })?.value;
  if (!component) return;
  const rootNode = extractTreeByName(component, element.source.rootName);
  const id = getDataAttr(rootNode, BlocksDataAttribute.CANONICAL_ELEMENT);
  return id;
}

/** Gets the canonical (master) element ID for a given element instance. */
export async function getCanonicalElementIdPromise(
  element: ElementInstance | ElementInstanceState,
  library: LibraryCache,
): Promise<string> {
  const { slug, rootName } = element.source;
  if (slug.startsWith('EL')) {
    return slug;
  }
  const component = await library.getPublicComponentJson(slug);
  const rootNode = extractTreeByName(component, rootName);
  const id = getDataAttr(rootNode, BlocksDataAttribute.CANONICAL_ELEMENT);
  if (!id) throw new Error(`No canonical found for ${slug} ${rootName}`);
  return id;
}

export function getIsElementNonDeletable(
  element: ElementInstance | ElementInstanceState,
  library: LibraryCache,
): boolean | undefined {
  const component = library.getCachedPublicComponentJson(element.source.slug, {
    fetchIfMissing: true,
  })?.value;
  if (!component) return;
  const rootNode = extractTreeByName(component, element.source.rootName);
  return !!getDataAttr(rootNode, BlocksDataAttribute.NON_DELETABLE);
}

export function maybeFindSlotsIn(
  source: ElementSourceState,
  getComponent: (slug: string) => Component | undefined,
): SlotInfo[] {
  const component = getComponent(source.slug);
  if (!component) return [];
  return findSlotsIn(source, component);
}

export function findSlotsInTree(
  rootElement: ElementInstance,
  library: LibraryCache,
): (SlotInfo & { element: ElementInstance })[] {
  const component = library.getCachedPublicComponentJson(
    rootElement.source.slug,
  )?.value;
  if (!component) return [];
  return findSlotsIn(rootElement.source, component).flatMap((slot) => {
    const slotWithElement = { ...slot, element: rootElement };
    const override = rootElement.slotOverrides.get(slot.name);
    if (!override) return [slotWithElement];
    return [
      slotWithElement,
      ...override.items.flatMap((item) => findSlotsInTree(item, library)),
    ];
  });
}

/**
 * Returns 1 layer of the elements tree.
 *
 * I.e. each returned slot's original items will not have any slots themselves.  The caller should call findSlotsIn again for those.
 **/
export function findSlotsIn(
  source: ElementSourceState,
  component: Component,
): SlotInfo[] {
  const element = extractTreeByName(component, source.rootName);

  function walk(node: NestedNode): SlotInfo[] {
    const slotInfo = getSlotInfoAtNode(node, source, component);
    if (slotInfo) {
      return [slotInfo];
    }
    const out: SlotInfo[] = [];
    for (const child of node.children) {
      out.push(...walk(child));
    }
    return out;
  }

  return walk(element);
}

/** If the node is a slot, returns the SlotInfo. */
export function getSlotInfoAtNode(
  node: NestedNode,
  source: ElementSourceState,
  component: Component,
): SlotInfo | undefined {
  if (node.text) return;

  const childrenSlotType = getDataAttr(node, BlocksDataAttribute.CHILDREN_SLOT);
  const slotName = getNodeName(node);
  // The self slots only get applied to non-root nodes.
  // (If we applied self slots on the root element, we'd infinitely recurse.)
  const selfSlotType =
    slotName === source.rootName
      ? undefined
      : getDataAttr(node, BlocksDataAttribute.SELF_SLOT);
  const slotType = childrenSlotType || selfSlotType;
  if (!slotType) return;

  if (!slotName) {
    throw new Error(
      `Slot node has no name: ${node.tag} / ${slotType} / ${node._id}`,
    );
  }

  const mechanism = selfSlotType ? 'self' : 'children';
  const children = mechanism === 'self' ? [node] : node.children;
  const originalItems = children.map((child) => {
    if (child.text) {
      throw new Error(
        // We can't express a text child as an ElementInstance, because
        // each ElementInstance has to correspond to a DOM node.
        `${slotName} (${node._id}) is not a valid slot, because it directly contains text children`,
      );
    }
    const margin = parseMarginWrapper(child, getMarginWrapperStyles(component));
    let unwrapped = child;
    if (margin) {
      if (child.children.length !== 1) {
        throw new Error(
          `In slot ${slotName}, margin wrapper ${child._id} must have exactly 1 child, but has ${child.children.length}`,
        );
      }
      const child0 = child.children[0];
      if (child0.text) {
        throw new Error(
          `In slot ${slotName}, margin wrapper ${child0._id} must have a non-text child, but has a text child`,
        );
      }
      unwrapped = child0;
    }
    const name = getNodeName(unwrapped);
    if (!name) {
      throw new Error(
        `In slot ${slotName}, a slot item element has no name: ${unwrapped.tag} / ${unwrapped._id}`,
      );
    }
    return {
      state: anElementInstance({
        source: {
          slug: source.slug,
          rootName: name,
        },
        margin,
      }),
      rootNode: unwrapped,
    };
  });
  return {
    name: slotName,
    nodeId: node._id,
    typeId: assertSlotTypeId(slotType),
    mechanism,
    originalItems,
  };
}

function assertSlotTypeId(slotType: string): `ST${string}` {
  if (!slotType.startsWith('ST')) {
    throw new Error(`Invalid slot type id: ${slotType}`);
  }
  return slotType as any;
}

const getMarginWrapperStyles = computedFn((component: Component) => {
  const out = new Map<string, string>();
  for (const style of component.payload.styles) {
    if (style.name.startsWith('margin-')) {
      out.set(style._id, style.name);
    }
  }
  return out;
});

function parseMarginWrapper(
  node: NestedTagNode,
  componentStyleIdToName: Map<string, string>,
): MarginState | undefined {
  let direction: MarginState['direction'] | undefined;
  let size: MarginState['size'] | undefined;

  for (const classId of node.classes) {
    const name =
      componentStyleIdToName.get(classId) ||
      styleGuideStyleById.get(classId)?.name;
    if (!name) continue;
    if (!name.startsWith('margin-')) {
      return undefined; // margin wrappers can only have margin-* classes
    }
    const suffix = name.slice('margin-'.length);
    if (MARGIN_SIZES.includes(suffix as any)) {
      size = suffix as any;
    } else if (MARGIN_DIRECTIONS.includes(suffix as any)) {
      direction = suffix as any;
    } else {
      throw new Error('Unknown margin- class: ' + name);
    }
  }
  if (direction && size) {
    return { direction, size };
  }
  if (direction) {
    throw new Error(
      `Margin wrapper has direction ${direction} but not size.  Class: ${node.classes}`,
    );
  }
  if (size) {
    throw new Error(
      `Margin wrapper has size ${size} but not direction.  Class: ${node.classes}`,
    );
  }
  return undefined;
}

export const canAddToSlot = (
  slotOverride: SlotOverride,
  slotType: SlotType,
): { result: false; reason: string } | { result: true } => {
  if (
    slotType.maxItems !== null &&
    slotOverride.items.length >= slotType.maxItems
  ) {
    return { result: false, reason: 'Maximum number of items reached' };
  }

  if (!slotType.name.startsWith('List')) {
    return { result: false, reason: 'Elements can’t be added here' };
  }

  return { result: true };
};

export const canReorderInSlot = (slotType: SlotType) => {
  return slotType.name.startsWith('List');
};

export const canDeleteInSlot = (slotType: SlotType) => {
  // Archive slots are used when we tagged something as a slot in the past, but
  // now want to revoke the ability to edit elements in it.
  // The data model means we can't delete the slot itself (references to it are
  // persisted in slotOverrides), so the next best thing is preventing deletion
  // of elements in it, so it'll behave as if the slot wasn't there in the site
  // builder. (Adding and reordering are already disabled since it's not a list
  // slot.)
  return !slotType.name.startsWith('Archive');
};

export const getParentSlotOfElement = ({
  rootElement,
  element,
  library,
}: {
  rootElement: ElementInstance;
  element: ElementInstance;
  library: LibraryCache;
}) => {
  const parentElement = parentElementWithinRoot(element, rootElement);
  if (!parentElement) return null;

  const tuple = Array.from(parentElement.slotOverrides.entries()).find(
    ([_, slot]) => slot.items.includes(element),
  );
  if (!tuple) return null;
  const [slotName, slotOverride] = tuple;

  const component = library.getCachedPublicComponentJson(
    parentElement.source.slug,
  )?.value;
  if (!component) return null;

  const slotInfos = findSlotsIn(parentElement.source, component);
  const slotInfo = slotInfos.find((info) => info.name === slotName);
  if (!slotInfo) return null;

  const slotType = library.getCachedSlotType(slotInfo.typeId)?.value;
  if (!slotType) return null;

  return { type: slotType, override: slotOverride, info: slotInfo };
};

export const isElementSelectable = ({
  element,
  parentSlotType,
  library,
}: {
  element: ElementInstance;
  parentSlotType: SlotType | undefined;
  library: LibraryCache;
}) => {
  // If the element is a leaf in the element tree (i.e. it has no slots of its
  // own), it's selectable.
  const comp = library.getCachedPublicComponentJson(element.source.slug)?.value;
  if (comp) {
    const slots = findSlotsIn(element.source, comp);
    if (slots.length === 0) return true;
  }

  // If the element's parent slot allows reordering, it's selectable, since we
  // want to allow selection of anything in a list, even if it has slots.
  if (parentSlotType && canReorderInSlot(parentSlotType)) return true;

  return false;
};

export const getElementResetMenuItem = (
  interactable: Interactable,
  editing: ElementNodeEditingActions,
): AnyMenuItem[] => {
  const resetElementsMenuItem = menuItem({
    label: 'Reset elements',
    disabled: editing.resetElements === undefined,
    onClick: () => {
      editing.resetElements?.();
      editing.analytics.track({
        event: 'blocks/reset',
        object: 'elements',
        subject: 'element',
        componentSlugs: [interactable.element.source.slug],
      });
    },
  });

  const resetCopyMenuItem = menuItem({
    label: 'Reset copy',
    disabled: editing.resetCopy === undefined,
    onClick: () => {
      editing.resetCopy?.();
      editing.analytics.track({
        event: 'blocks/reset',
        object: 'copy',
        subject: interactable.text ? 'node' : 'element',
        componentSlugs: [interactable.element.source.slug],
      });
    },
  });

  const resetAllMenuItem = menuItem({
    label: 'Reset all changes',
    disabled:
      editing.resetElements === undefined && editing.resetCopy === undefined,
    onClick: () => {
      editing.resetElements?.();
      editing.resetCopy?.();
      editing.analytics.track({
        event: 'blocks/reset',
        object: 'all',
        subject: interactable.text ? 'node' : 'element',
        componentSlugs: [interactable.element.source.slug],
      });
    },
  });

  const hasSlots = editing.mySlots.length > 0;
  const hasText = doesInteractableTreeContainText(interactable);

  if (hasSlots && hasText) {
    return [
      subMenu({
        label: 'Reset',
        menu: [resetAllMenuItem, resetElementsMenuItem, resetCopyMenuItem],
      }),
    ];
  }

  if (hasSlots) return [resetElementsMenuItem];
  if (hasText) return [resetCopyMenuItem];
  return [];
};
